import { AionzDialog, LoadingService } from '@aionzdev/design-system';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Injector, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { finalize } from 'rxjs';
import { PurchaseRequestDocumentUploadDialogData } from '../../../interfaces/purchase-request-document-upload-dialog-data';
import { PurchaseRequestOptionDialogAction } from '../../../interfaces/purchase-request-option-dialog-result';
import { PurchaseRequestOptionDialogData } from '../../../interfaces/purchase-request-option-dialog-data';
import { AbstractPurchaseRequestDocumentService } from '../../../services/abstract-purchase-request-document-upload.service';
import { PurchaseRequestDocumentService } from '../../../services/purchase-request-document-upload.service';
import { PurchaseRequestDocumentUploadDialogComponent } from '../purchase-request-document-upload-dialog/purchase-request-document-upload-dialog.component';
import { PurchaseRequestOptionDialogComponent } from '../purchase-request-option-dialog/purchase-request-option-dialog.component';

@Component({
  selector: 'aionz-quotation-map-pending-action-button',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './quotation-map-pending-action-button.component.html',
  styleUrls: ['./quotation-map-pending-action-button.component.scss'],
  providers: [
    {
      provide: AbstractPurchaseRequestDocumentService,
      useClass: PurchaseRequestDocumentService
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotationMapPendingActionButtonComponent {
  public readonly purchaseId = input.required<string>();
  public readonly supplierId = input.required<string>();
  /** Nome e CNPJ do fornecedor: aparecem nos dialogs e no nome do arquivo baixado. */
  public readonly supplierName = input<string>('');
  public readonly supplierCnpj = input<string>('');

  private readonly dialog = inject(AionzDialog);
  private readonly injector = inject(Injector);
  private readonly loading = inject(LoadingService);
  private readonly documentService = inject(AbstractPurchaseRequestDocumentService);

  protected handleClick(): void {
    const dialogRef = this.dialog.open(PurchaseRequestOptionDialogComponent, {
      data: { title: this.dialogTitle() } as PurchaseRequestOptionDialogData
    });

    dialogRef.componentInstance.onDownloadOrder.subscribe(() => {
      this.loading.showLoading();
      this.documentService
        .downloadDocuments(this.purchaseId(), this.supplierId())
        .pipe(finalize(() => this.loading.hideLoading()))
        .subscribe(blob => this.downloadFile(blob));
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === PurchaseRequestOptionDialogAction.UPLOAD_SPREADSHEET) {
        const uploadDialogRef = this.dialog.open(PurchaseRequestDocumentUploadDialogComponent, {
          data: {
            purchaseId: this.purchaseId(),
            supplierId: this.supplierId(),
            title: this.dialogTitle()
          } as PurchaseRequestDocumentUploadDialogData,
          injector: this.injector,
        });

        uploadDialogRef.afterClosed().subscribe();
      }
    });
  }

  private dialogTitle(): string {
    const name = this.supplierName().trim();
    return name ? `Enviar proposta de ${name}` : 'Enviar proposta do fornecedor';
  }

  /**
   * Nome de arquivo que identifica o fornecedor: `pedido-{solicitação}-{FORNECEDOR}-{CNPJ}.xlsx`.
   * O id interno do fornecedor não diz nada a quem baixa e levava a subir a planilha na coluna errada.
   */
  private buildFileName(): string {
    const slug = slugify(this.supplierName());
    const digits = this.supplierCnpj().replace(/\D/g, '');
    const supplierPart = [slug, digits].filter(Boolean).join('-') || this.supplierId();
    return `pedido-${this.purchaseId()}-${supplierPart}.xlsx`;
  }

  private downloadFile(blob: Blob): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = this.buildFileName();
    anchor.click();
    URL.revokeObjectURL(url);
  }
}

/** "PETRORIO Comércio de Combustíveis Ltda" -> "PETRORIO-COMERCIO-DE-COMBUSTIVEIS-LTDA" (máx. 60 caracteres). */
function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
}
